<template>
  <div>
    <b-loading :is-full-page="true" :active="!initialized"></b-loading>
    <section v-if="initialized" class="section is-main-section">
      <div class="section has-text-centered">
        <h1 class="title">Manage Channel Schedule</h1>
        <h2 v-if="channel && channel.name" class="subtitle has-text-info" style="margin-top: -10px;">
          <b-icon icon="television" size="is-small" />
          {{ channel.name }}
        </h2>
      </div>
      <div class="columns is-vcentered">
        <div class="column is-12">
          <div class="flex-card light-bordered light-raised">
            <div class="has-margin-bottom-15 buttons">
              <!-- Back to Channels List -->
              <b-button
                type="is-light"
                icon-left="arrow-left"
                tag="router-link"
                :to="{ name: 'ManageChannels' }"
              >
                Back to Channels
              </b-button>
              <!-- View Toggle Buttons -->
              <b-button
                :type="currentView === 'week' ? 'is-info' : 'is-light'"
                icon-left="calendar-week"
                @click="switchToWeekView"
              >
                Week View
              </b-button>
              <b-button
                :type="currentView === 'day' ? 'is-info' : 'is-light'"
                icon-left="calendar-day"
                @click="switchToDayView"
              >
                Day View
              </b-button>
              <b-button
                type="is-light"
                icon-left="calendar-today"
                @click="jumpToToday"
                title="Jump to current week / day"
              >
                Today
              </b-button>

              <b-button
                type="is-primary"
                :disabled="loading"
                :class="{ 'is-loading': loading }"
                @click="addToSchedule"
              >
                Add Scheduled Title</b-button
              >
              <b-button
                type="is-warning"
                icon-left="history"
                @click="showRecentChanges"
              >
                Recent Changes</b-button
              >
              <!-- Week view only buttons -->
              <b-button
                v-if="currentView !== 'day'"
                type="is-primary"
                :disabled="loading"
                :class="{ 'is-loading': loading }"
                @click="exportEpg"
              >
                Export EPG</b-button
              >
              <b-dropdown v-if="currentView === 'day'" aria-role="list">
                <template #trigger>
                  <b-button
                    type="is-primary"
                    :disabled="loading"
                    :class="{ 'is-loading': loading }"
                    icon-right="menu-down"
                  >
                    Export Playout
                  </b-button>
                </template>
                <b-dropdown-item aria-role="listitem" @click="exportPlayoutMCRList">
                  MCR List
                </b-dropdown-item>
                <b-dropdown-item aria-role="listitem" @click="exportPlayoutCSV">
                  CSV
                </b-dropdown-item>
                <b-dropdown-item aria-role="listitem" @click="exportPlayoutBXF">
                  BXF
                </b-dropdown-item>
              </b-dropdown>
              <b-button
                v-if="currentView !== 'day'"
                type="is-primary"
                :disabled="loading"
                :class="{ 'is-loading': loading }"
                @click="exportNielsen"
              >
                Export Nielsen</b-button
              >
              <b-button
                v-if="currentView !== 'day'"
                type="is-primary"
                :disabled="loading"
                :class="{ 'is-loading': loading }"
                @click="exportBreakSchedule"
              >
                Export Break Schedule</b-button
              >
              <b-button
                v-if="currentView !== 'day'"
                type="is-primary"
                :disabled="loading"
                :class="{ 'is-loading': loading }"
                @click="exportProduction"
              >
                Export Production</b-button
              >
              <b-button
                v-if="currentView !== 'day'"
                type="is-primary"
                :disabled="loading"
                :class="{ 'is-loading': loading }"
                @click="copyWeek"
              >
                Copy Week</b-button
              >
            </div>
            <div class="card-body">
              <!-- Stale Data Warning Banner -->
              <div v-if="currentView === 'day' && isDataStale && !staleWarningDismissed" class="stale-data-warning">
                <div class="stale-warning-content">
                  <b-icon icon="alert-circle" size="is-small"></b-icon>
                  <span class="stale-warning-text">
                    Schedule has been updated by another user. Your view may be out of date.
                  </span>
                  <div class="stale-warning-actions">
                    <b-button
                      type="is-warning"
                      size="is-small"
                      icon-left="refresh"
                      @click="refreshStaleData"
                    >
                      Refresh Now
                    </b-button>
                    <b-button
                      type="is-light"
                      size="is-small"
                      @click="dismissStaleWarning"
                    >
                      Dismiss
                    </b-button>
                  </div>
                </div>
              </div>

              <!-- Day view layout with media library panel -->
              <div v-if="currentView === 'day'" class="day-view-layout">
                <!-- Media Library Panel -->
                <MediaLibraryPanel
                  :channel-id="channelId"
                  :channel-name="channel ? channel.name : ''"
                  :disabled="isDataStale && !staleWarningDismissed"
                  :replace-mode="isReplaceMode"
                  @drag-start="handleMediaDragStart"
                  @drag-end="handleMediaDragEnd"
                  @replacement-selected="handleReplacementSelected"
                />

                <!-- Broadcast Timeline -->
                <BroadcastTimeline
                  ref="timeline"
                  :items="events"
                  :current-date="currentDate"
                  :is-dragging="isDraggingMedia"
                  :dragging-item-duration="draggingMediaDuration"
                  :is-stale="isDataStale && !staleWarningDismissed"
                  :loading="loading"
                  :channel="channel"
                  @media-drop="handleTimelineMediaDrop"
                  @media-reposition="handleTimelineMediaReposition"
                  @item-click="handleTimelineItemClick"
                  @item-delete="handleTimelineItemDelete"
                  @items-bulk-delete="handleTimelineBulkDelete"
                  @replace-mode-start="handleReplaceModeStart"
                  @items-bulk-replace="handleTimelineBulkReplace"
                  @date-change="handleDateChange"
                  @copy-day-shortform="handleCopyDayShortform"
                />
              </div>

              <!-- Week view (existing) -->
              <VueCal v-else
                style="height: 800px"
                :selected-date="currentDate"
                active-view="week"
                :disable-views="['years', 'year', 'month']"
                :time-from="0 * 60"
                :time-to="24 * 60"
                :time-step="30"
                :snap-to-time="30"
                :time-cell-height="timeCellHeight"
                :hide-weekends="false"
                :twelve-hour="false"
                :hide-view-selector="true"
                :hide-title-bar="false"
                :show-all-day-events="false"
                :sticky-split-labels="true"
                :events="events"
                :special-hours="specialHours"
                :editable-events="{
                  title: false,
                  drag: true,
                  resize: false,
                  delete: false,
                  create: false
                }"
                :on-event-click="currentView === 'day' ? () => {} : eventClick"
                :on-event-create="eventCreate"
                :drag-to-create-threshold="0"
                @cell-dblclick="addEventOnClick"
                @ready="fetchEvents"
                @view-change="fetchEvents"
                @event-drop="eventDrop"
              >
                <template #event="{ event }">
                  <div
                    v-if="currentView === 'day'"
                    class="vuecal__event-title day-view-event"
                  >
                    <template v-if="event.item_type === 'segment'">
                      <div class="event-main-title">{{ event.media_title }}</div>
                      <div v-if="event.season_number || event.episode_number" class="event-meta">
                        <span v-if="event.season_number">S{{ event.season_number }}</span>
                        <span v-if="event.episode_number">E{{ event.episode_number }}</span>
                        <span v-if="event.segment_number"> • Seg {{ event.segment_number }}</span>
                      </div>
                      <div v-if="event.duration_seconds" class="event-duration">
                        {{ Math.floor(event.duration_seconds / 60) }}:{{ String(event.duration_seconds % 60).padStart(2, '0') }}
                      </div>
                    </template>
                    <template v-else>
                      <div class="event-main-title">
                        <span class="break-icon">📺</span> {{ event.title }}
                      </div>
                      <div v-if="event.duration_seconds" class="event-duration">
                        {{ Math.floor(event.duration_seconds / 60) }}:{{ String(event.duration_seconds % 60).padStart(2, '0') }}
                      </div>
                    </template>
                  </div>
                  <div
                    v-else
                    class="vuecal__event-title week-view-event"
                    style="font-size: 12px; padding: 2px;"
                  >
                    {{ event.title }}
                    <div v-if="event.episode_title" class="episode-title-subtitle">
                      {{ event.episode_title }}
                    </div>
                    <span
                      v-if="event.episode_id"
                      class="tx-status-dot"
                      :class="isEpisodeTxReady(event) ? 'tx-ready' : 'tx-missing'"
                    ></span>
                  </div>
                </template>
              </VueCal>
            </div>
          </div>
        </div>
      </div>
    </section>
    <ExportScheduleModal
      v-if="showExportScheduleModal"
      :show="showExportScheduleModal"
      :start="lastStartDate"
      :end="lastEndDate"
      :channel-id="channelId"
      :report-type="reportType"
      :channel-name="channel.name"
      @close-modal="showExportScheduleModal = false"
    >
    </ExportScheduleModal>
    <ExportPlayoutScheduleModal
      v-if="showExportPlayoutScheduleModal"
      :show="showExportPlayoutScheduleModal"
      :channel-id="channelId"
      :channel-name="channel.name"
      @close-modal="showExportPlayoutScheduleModal = false"
    >
    </ExportPlayoutScheduleModal>
    <AddToScheduleModal
      v-if="showAddToScheduleModal"
      :show="showAddToScheduleModal"
      :start="start"
      :channel-id="channelId"
      :item="item"
      @refresh="refreshData"
      @close-modal="showAddToScheduleModal = false"
    ></AddToScheduleModal>
    <CopyScheduleWeekModal
      v-if="showCopyScheduleWeekModal"
      :show="showCopyScheduleWeekModal"
      :start-date="lastStartDate"
      :end-date="lastEndDate"
      :channel-id="channelId"
      @refresh="refreshData"
      @close-modal="showCopyScheduleWeekModal = false"
    ></CopyScheduleWeekModal>
    <RecentChangesModal
      :show="showRecentChangesModal"
      @refresh="refreshData"
      @close-modal="showRecentChangesModal = false"
    ></RecentChangesModal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  getHours,
  getMinutes,
  addMinutes,
  setMinutes,
  setSeconds,
  setMilliseconds,
  format
} from 'date-fns'
import ScheduleApi from '@/api/schedules'
import { isEpisodeTxReady } from '@/utils/episodeStatus'
import PlaylistApi from '@/api/playlist'
import CardComponent from '@/components/CardComponent'
import AddToScheduleModal from '@/components/AddToScheduleModal'
import ExportScheduleModal from '@/components/ExportScheduleModal'
import ExportPlayoutScheduleModal from '@/components/ExportPlayoutScheduleModal'
import CopyScheduleWeekModal from '@/components/CopyScheduleWeekModal'
import RecentChangesModal from '@/components/RecentChangesModal'
import MediaLibraryPanel from '@/components/MediaLibraryPanel'
import ChannelApi from '@/api/channels'
import ChannelViewingSlotApi from '@/api/channelViewingSlots'
import VueCal from 'vue-cal'
import 'vue-cal/dist/vuecal.css'
// Import drag-and-drop module explicitly to prevent dynamic load 404 error
import 'vue-cal/dist/drag-and-drop'
import BroadcastTimeline from '@/components/BroadcastTimeline.vue'

export default {
  name: 'EditSchedule',
  components: {
    AddToScheduleModal,
    ExportScheduleModal,
    CardComponent,
    VueCal,
    CopyScheduleWeekModal,
    ExportPlayoutScheduleModal,
    RecentChangesModal,
    MediaLibraryPanel,
    BroadcastTimeline
  },
  metaInfo: {
    title: 'Edit Schedule'
  },
  computed: {
    ...mapGetters(['user']),
    timeCellHeight() {
      // In day view, use taller cells to show segment details
      // In week view, use compact cells
      if (this.currentView === 'day') {
        // Apply zoom level: 80px (min) to 200px (max)
        return 80 + (this.dayViewZoom * 40)
      }
      return 40
    },
    availableGaps() {
      return this.calculateTimeGaps()
    }
  },
  async mounted() {
    await this.loadSpecialHours()
    this.channel = await ChannelApi.getById(this.channelId)

    // Restore saved view state (view mode + date) from sessionStorage
    // This ensures day view persists after page refresh
    this.restoreViewState()

    this.initialized = true

    // If restored to day view, fetch day view data using the restored currentDate
    // and start polling for stale-data detection. (Polling-on-mount was removed in
    // 0a4587b on 2026-01-20; restored 2026-05-02 — without it, the "schedule has
    // been updated" banner never fires when day view is reached via page refresh
    // or returning from Edit Episode, since switchToDayView() is bypassed.)
    if (this.currentView === 'day') {
      await this.fetchDayPlaylistEvents(this.currentDate)
      this.startSchedulePolling()
    }
  },
  beforeDestroy() {
    // Clean up polling interval
    this.stopSchedulePolling()
    // Save current state before leaving
    this.saveViewState()
  },
  data() {
    return {
      loading: false,
      initialized: false,
      showAddToScheduleModal: false,
      showExportScheduleModal: false,
      showExportPlayoutScheduleModal: false,
      showCopyScheduleWeekModal: false,
      showRecentChangesModal: false,
      start: '',
      lastStartDate: '',
      lastEndDate: '',
      channelId: parseInt(this.$route.params.id, 10),
      events: [],
      item: {},
      specialHours: {},
      reportType: '',
      channel: {},
      currentDate: new Date(),
      currentView: 'week',
      playlistGenerated: {},
      dayViewZoom: 1,  // 0=80px, 1=120px, 2=160px, 3=200px
      isDraggingMedia: false,
      draggingMediaDuration: 0, // Duration in seconds of item being dragged from media library
      isReplaceMode: false,
      // Stale data detection
      scheduleChecksum: null,
      lastFetchTime: null,
      isDataStale: false,
      pollIntervalId: null,
      staleWarningDismissed: false
    }
  },
  methods: {
    ...mapActions(['getUser']),
    isEpisodeTxReady,

    // Format weekday name for heading
    formatWeekday(date) {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      return days[date.getDay()]
    },

    // Switch between week and day views
    switchToWeekView() {
      this.stopSchedulePolling()
      this.currentView = 'week'
      this.isDataStale = false
      this.staleWarningDismissed = false
      this.saveViewState()
      this.fetchEvents({ view: 'week', startDate: this.lastStartDate, endDate: this.lastEndDate })
    },

    switchToDayView() {
      this.currentView = 'day'

      // If we have a lastStartDate from week view, use it
      // Otherwise fall back to current date
      if (this.lastStartDate) {
        this.currentDate = new Date(this.lastStartDate)
      }

      this.saveViewState()
      this.fetchDayPlaylistEvents(this.currentDate)
      this.startSchedulePolling()
    },

    // Jump back to current week (week view) or current day (day view).
    // Counterpart to the persistence behavior: after building advance schedules
    // operators need an explicit way to return to today without manually
    // clicking VueCal's prev arrows many times.
    jumpToToday() {
      this.currentDate = new Date()
      this.saveViewState()
      if (this.currentView === 'day') {
        this.fetchDayPlaylistEvents(this.currentDate)
      }
      // For week view: VueCal's @view-change fires when :selected-date moves
      // to a different week → fetchEvents runs automatically with today's range
    },

    // State preservation for page refresh
    saveViewState() {
      const state = {
        view: this.currentView,
        date: this.currentDate.toISOString(),
        channelId: this.channelId
      }
      sessionStorage.setItem(`schedule_view_${this.channelId}`, JSON.stringify(state))
    },

    restoreViewState() {
      try {
        const savedState = sessionStorage.getItem(`schedule_view_${this.channelId}`)
        if (savedState) {
          const state = JSON.parse(savedState)
          if (state.channelId === this.channelId) {
            this.currentView = state.view || 'week'
            this.currentDate = state.date ? new Date(state.date) : new Date()
            return // Successfully restored
          }
        }
      } catch (e) {
        console.warn('Could not restore view state:', e)
      }
      // Default to week view if no saved state or error
      this.currentView = 'week'
      this.currentDate = new Date()
    },

    // Schedule change polling
    startSchedulePolling() {
      // Only poll in day view
      if (this.currentView !== 'day') return

      // Clear any existing interval
      this.stopSchedulePolling()

      // Poll every 30 seconds
      this.pollIntervalId = setInterval(() => {
        this.checkForScheduleChanges()
      }, 30000)
    },

    stopSchedulePolling() {
      if (this.pollIntervalId) {
        clearInterval(this.pollIntervalId)
        this.pollIntervalId = null
      }
    },

    async checkForScheduleChanges() {
      if (this.currentView !== 'day' || !this.currentDate) return

      try {
        const dateStr = format(this.currentDate, 'yyyy-MM-dd')
        const changes = await ScheduleApi.getScheduleChanges(this.channelId, dateStr)

        // Compare checksum - if different, data has changed
        if (this.scheduleChecksum && changes.checksum !== this.scheduleChecksum) {
          this.isDataStale = true
          this.staleWarningDismissed = false
        }
      } catch (error) {
        console.warn('Schedule change check failed:', error)
      }
    },

    async refreshStaleData() {
      this.isDataStale = false
      this.staleWarningDismissed = false
      await this.fetchDayPlaylistEvents(this.currentDate)
      this.$buefy.toast.open({
        message: 'Schedule refreshed',
        type: 'is-success',
        duration: 2000
      })
    },

    dismissStaleWarning() {
      this.staleWarningDismissed = true
    },

    async loadSpecialHours() {
      const viewingSlots = await ChannelViewingSlotApi.getByChannel(
        this.channelId
      )
      let slotNo = 0
      viewingSlots.forEach(slot => {
        const days = slot.days.split(',').map(item => parseInt(item, 10))
        days.forEach(day => {
          if (!this.specialHours[day]) {
            this.specialHours[day] = []
          }
          this.specialHours[day].push({
            from: this.getSpecialHours(slot.start_time),
            to: this.getSpecialHours(slot.end_time),
            class: `special-hours-${slotNo}`
          })
        })
        slotNo++
        if (slotNo > 9) {
          slotNo = 0
        }
      })
    },
    getSpecialHours(timeValue) {
      // Handle TIME strings (HH:mm:ss) from database
      if (typeof timeValue === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(timeValue)) {
        const [hours, minutes] = timeValue.split(':')
        return parseInt(hours, 10) * 60 + parseInt(minutes, 10)
      }
      // Handle Date objects or full datetime strings
      const date = new Date(timeValue)
      return getHours(date) * 60 + getMinutes(date)
    },
    eventCreate(event, deleteFn) {
      deleteFn()
    },
    async eventDrop({ event, originalEvent, external }) {
      try {
        event.start_time = event.start
        event.end_time = event.end
        await ScheduleApi.update(event)

        // Refresh events to show new position
        await this.refreshData()

        this.$buefy.toast.open({
          message: 'Schedule updated successfully!',
          position: 'is-top',
          type: 'is-success'
        })
      } catch (error) {
        // Revert on error by refreshing from server
        await this.refreshData()
        this.$buefy.toast.open({
          message: 'Failed to update schedule',
          position: 'is-top',
          type: 'is-danger'
        })
      }
    },
    eventClick(currentevent) {
      this.start = currentevent.start
      this.item = currentevent
      this.showAddToScheduleModal = true
    },
    async refreshData() {
      // After adding/editing schedule items, always return to week view
      if (this.currentView === 'day') {
        this.switchToWeekView()
      } else {
        await this.fetchEvents({
          view: this.currentView,
          startDate: this.lastStartDate,
          endDate: this.lastEndDate
        })
      }
    },
    async exportPlayoutMCRList() {
      // MCR List is the current/default playout format
      this.showExportPlayoutScheduleModal = true
    },
    async exportPlayoutCSV() {
      // TODO: Implement CSV export format
      this.$buefy.toast.open({
        message: 'CSV export coming soon',
        type: 'is-info',
        duration: 3000
      })
    },
    async exportPlayoutBXF() {
      // BXF (Broadcast Exchange Format) - SMPTE ST 2021
      // Universal format for importing into various playout/automation systems
      // Uses current channel from route params and currentDate from day view
      if (!this.channelId || !this.currentDate) {
        this.$buefy.toast.open({
          message: 'Channel and date not available',
          type: 'is-warning',
          duration: 3000
        })
        return
      }

      try {
        this.$buefy.toast.open({
          message: 'Generating BXF export...',
          type: 'is-info',
          duration: 2000
        })

        await ScheduleApi.exportBXF(
          this.channelId,
          this.currentDate,
          this.channel?.name || `Channel-${this.channelId}`
        )

        this.$buefy.toast.open({
          message: 'BXF file downloaded successfully',
          type: 'is-success',
          duration: 3000
        })
      } catch (error) {
        console.error('BXF export error:', error)
        this.$buefy.toast.open({
          message: error?.data?.error || 'Failed to export BXF',
          type: 'is-danger',
          duration: 5000
        })
      }
    },
    async exportEpg() {
      this.showExportScheduleModal = true
      this.reportType = 'epg'
    },
    async exportNielsen() {
      this.showExportScheduleModal = true
      this.reportType = 'nielsen'
    },
    async exportBreakSchedule() {
      this.showExportScheduleModal = true
      this.reportType = 'break'
    },
    async exportProduction() {
      this.showExportScheduleModal = true
      this.reportType = 'production'
    },
    async fetchEvents({ view, startDate, endDate, week } = {}) {
      // Guard bare calls (e.g. switchToWeekView() after a save): a missing arg
      // must never throw on destructuring. Fall back to the last known range.
      startDate = startDate || this.lastStartDate || this.currentDate
      endDate = endDate || this.lastEndDate
      console.log('fetchEvents called with view:', view, 'date:', startDate)
      this.lastStartDate = startDate
      this.lastEndDate = endDate
      // Keep currentDate in sync with the week the user is viewing in VueCal.
      // Without this, two bugs occur:
      // 1. After refreshData() re-renders, :selected-date snaps VueCal back to today
      // 2. Page refresh restores the old saved currentDate (today) instead of the
      //    week the user navigated to
      if (startDate) {
        this.currentDate = new Date(startDate)
        this.saveViewState()
      }
      // Preserve current view if not explicitly passed
      if (view) {
        this.currentView = view
      }

      // If day view, generate playlist with segments and auto-fill breaks
      if (this.currentView === 'day') {
        console.log('Switching to day view, fetching playlist...')
        await this.fetchDayPlaylistEvents(startDate)
        return
      }

      console.log('Week view - showing regular schedule')

      // Week view - show episodes as single entries
      let items = await ScheduleApi.getByChannel(
        this.$route.params.id,
        startDate,
        endDate
      )
      let slotNo = 0
      this.events = items.map(item => {
        let mediadata = null
        if (item.mediadata) {
          mediadata = JSON.parse(item.mediadata)
        }
        slotNo++
        if (slotNo > 9) {
          slotNo = 0
        }
        return {
          title:
            item.media_title +
            (item.media_type === 'series' && mediadata && mediadata.seriesNo
              ? ` S${mediadata.seriesNo}`
              : '') +
            (item.episode_number ? ` E${item.episode_number}` : ''),
          episode_title: item.episode_title || '',
          // Backend already converted to channel timezone, just use as-is
          start: new Date(item.start_time),
          end: new Date(item.end_time),
          media_id: item.media_id,
          episode_id: item.episode_id,
          id: item.id,
          class: `${item.media_type}-${slotNo}`,
          repeats_id: item.repeats_id,
          // TX-readiness fields for the dot indicator (week view only)
          tx_workflow_status: item.tx_workflow_status,
          stitched_file_path: item.stitched_file_path,
          transcoded_file_path: item.transcoded_file_path,
          storage_path: item.storage_path
        }
      })
      // DIAG 2026-06: trace post-Add re-render bug. Dump events with computed
      // duration so we can see what VueCal is actually receiving vs what DB has.
      // Remove once stinky bug is found.
      console.log('[fetchEvents] events array length:', this.events.length)
      const recent = [...this.events].sort((a, b) => b.id - a.id).slice(0, 3)
      recent.forEach(e => {
        const durMs = e.end.getTime() - e.start.getTime()
        const durMin = Math.round(durMs / 60000)
        console.log(`[fetchEvents] id=${e.id} title=${e.title} start=${e.start.toISOString()} end=${e.end.toISOString()} duration=${durMin}min`)
      })
    },

    async fetchDayPlaylistEvents(date) {
      const dateStr = format(new Date(date), 'yyyy-MM-dd')

      try {
        // Use the day view API which reuses existing break schedule logic
        const dayViewItems = await ScheduleApi.getDayView(this.channelId, dateStr)

        console.log('Day view loaded:', dayViewItems.length, 'items')

        if (!dayViewItems || dayViewItems.length === 0) {
          console.warn('No day view items returned, showing empty day')
          this.events = []
          this.scheduleChecksum = 'empty'
          this.lastFetchTime = new Date()
          this.isDataStale = false
          return
        }

        // Convert to VueCal events - pass through all metadata
        // Filter out gap indicators as they are only for the timeline component
        this.events = dayViewItems
          .filter(item => !item.isGap)
          .map((item, index) => {
            return {
              ...item,  // Include all backend data
              start: new Date(item.start),
              end: new Date(item.end),
              id: item.schedule_id + '-' + (item.segment_number || `break-${index}`),
              class: item.item_type,
              editable: !item.is_locked
            }
          })

        // Fetch and store the checksum for change detection
        try {
          const changes = await ScheduleApi.getScheduleChanges(this.channelId, dateStr)
          this.scheduleChecksum = changes.checksum
          this.lastFetchTime = new Date()
          this.isDataStale = false
        } catch (e) {
          console.warn('Could not fetch schedule checksum:', e)
        }

        console.log('Day view events created:', this.events.length)
        console.log('Break events:', this.events.filter(e => e.item_type === 'break').length)
      } catch (error) {
        console.error('Failed to load day view:', error)
        // Fallback to regular schedule
        let items = await ScheduleApi.getByChannel(
          this.$route.params.id,
          date,
          date
        )
        console.log('Fallback to schedule items:', items)
        this.events = items.map(item => ({
          title: item.media_title,
          start: new Date(item.start_time),
          end: new Date(item.end_time),
          media_id: item.media_id,
          episode_id: item.episode_id,
          id: item.id
        }))
      }
    },

    getNextAvailableTime() {
      // Get current time
      let now = new Date()

      // Reset seconds and milliseconds
      now = setSeconds(now, 0)
      now = setMilliseconds(now, 0)

      // Get current minutes
      const minutes = getMinutes(now)

      // Round up to next 30-minute interval
      const remainder = minutes % 30
      if (remainder !== 0) {
        now = addMinutes(now, 30 - remainder)
      } else {
        // If already on 30-minute mark, add 30 minutes to ensure it's in the future
        now = addMinutes(now, 30)
      }

      return now
    },
    addToSchedule() {
      this.start = this.getNextAvailableTime()
      this.item = undefined
      this.showAddToScheduleModal = true
    },
    addEventOnClick($event) {
      // If clicked time is in the past, use next available time
      const clickedTime = new Date($event)
      const now = new Date()

      if (clickedTime < now) {
        this.start = this.getNextAvailableTime()
      } else {
        this.start = $event
      }

      this.item = undefined
      this.showAddToScheduleModal = true
    },
    copyWeek() {
      this.showCopyScheduleWeekModal = true
    },
    showRecentChanges() {
      this.showRecentChangesModal = true
    },

    // Get style for drop zone positioning
    getDropZoneStyle(gap) {
      const dayStart = new Date(this.currentDate)
      dayStart.setHours(0, 0, 0, 0)

      // Calculate minutes from start of day
      const gapStartMinutes = (gap.start - dayStart) / 60000
      const gapDurationMinutes = gap.durationMinutes

      // Calculate pixel position (based on time cell height)
      const minutesPerCell = 30  // VueCal cells are 30 minutes
      const pixelsPerMinute = this.timeCellHeight / minutesPerCell

      const top = gapStartMinutes * pixelsPerMinute
      const height = gapDurationMinutes * pixelsPerMinute

      return {
        top: `${top}px`,
        height: `${height}px`
      }
    },

    // Calculate available time gaps in the schedule for inserting media
    calculateTimeGaps() {
      if (this.currentView !== 'day' || !this.events || this.events.length === 0) {
        return []
      }

      const gaps = []
      const dayStart = new Date(this.currentDate)
      dayStart.setHours(0, 0, 0, 0)
      const dayEnd = new Date(this.currentDate)
      dayEnd.setHours(23, 59, 59, 999)

      // Sort events by start time
      const sortedEvents = [...this.events].sort((a, b) => a.start - b.start)

      // Check gap at start of day
      if (sortedEvents[0] && sortedEvents[0].start > dayStart) {
        gaps.push({
          start: dayStart,
          end: sortedEvents[0].start,
          durationMinutes: (sortedEvents[0].start - dayStart) / 60000
        })
      }

      // Check gaps between events
      for (let i = 0; i < sortedEvents.length - 1; i++) {
        const currentEnd = sortedEvents[i].end
        const nextStart = sortedEvents[i + 1].start

        if (nextStart > currentEnd) {
          const durationMinutes = (nextStart - currentEnd) / 60000
          if (durationMinutes >= 0.5) { // At least 30 seconds
            gaps.push({
              start: currentEnd,
              end: nextStart,
              durationMinutes
            })
          }
        }
      }

      // Check gap at end of day
      const lastEvent = sortedEvents[sortedEvents.length - 1]
      if (lastEvent && lastEvent.end < dayEnd) {
        gaps.push({
          start: lastEvent.end,
          end: dayEnd,
          durationMinutes: (dayEnd - lastEvent.end) / 60000
        })
      }

      return gaps
    },

    // Handle media drop into schedule
    async handleMediaDrop(event, gap) {
      try {
        const mediaData = JSON.parse(event.dataTransfer.getData('application/json'))

        // duration_seconds is canonical (backend-computed from duration_ms);
        // fall back to mediaData.duration for legacy callers / unprobed rows.
        const durationSeconds = mediaData.duration_seconds != null
          ? parseInt(mediaData.duration_seconds, 10)
          : (parseInt(mediaData.duration, 10) || 0)

        const mediaDurationMinutes = durationSeconds / 60
        if (mediaDurationMinutes > gap.durationMinutes) {
          this.$buefy.toast.open({
            message: `Media too long (${Math.ceil(mediaDurationMinutes)} min). Only ${Math.floor(gap.durationMinutes)} min available.`,
            type: 'is-danger',
            duration: 3000
          })
          return
        }

        // Insert media at gap start
        await ScheduleApi.insertMedia({
          channel_id: this.channelId,
          media_id: mediaData.media_id,
          start_time: gap.start,
          duration: durationSeconds
        })

        // Refresh day view
        await this.fetchDayPlaylistEvents(this.currentDate)

        this.$buefy.toast.open({
          message: `${mediaData.title} inserted successfully`,
          type: 'is-success',
          duration: 3000
        })
      } catch (error) {
        console.error('Failed to insert media:', error)
        this.$buefy.toast.open({
          message: 'Failed to insert media. Please try again.',
          type: 'is-danger',
          duration: 3000
        })
      }
    },

    // Handle media drop from timeline component
    async handleTimelineMediaDrop({ media, gap, insertTime }) {
      console.log('Dropping media:', media, 'at time:', insertTime)

      // OPTIMISTIC UI: Add item to events immediately so user sees instant feedback.
      // duration_seconds is canonical (backend-computed from duration_ms); fall
      // back to media.duration only when unprobed/legacy.
      const optimisticId = `temp-${Date.now()}`
      const durationSeconds = media.duration_seconds != null
        ? parseInt(media.duration_seconds, 10)
        : (parseInt(media.duration, 10) || 0)
      const endTime = new Date(insertTime.getTime() + (durationSeconds * 1000))

      const optimisticItem = {
        schedule_id: optimisticId,
        media_id: media.media_id,
        title: media.title,
        start: format(insertTime, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
        end: format(endTime, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
        duration_seconds: durationSeconds,
        item_type: 'media',
        media_type: media.media_type,
        media_title: media.title,
        _optimistic: true // Flag to identify optimistic items
      }

      // Insert optimistically into events array at correct position
      const insertIdx = this.events.findIndex(e => new Date(e.start) > insertTime)
      if (insertIdx === -1) {
        this.events.push(optimisticItem)
      } else {
        this.events.splice(insertIdx, 0, optimisticItem)
      }

      try {
        // CRITICAL: Use local ISO format without 'Z' suffix to prevent timezone shift.
        // duration sent in SECONDS — backend insertMedia treats it as seconds.
        const payload = {
          channel_id: this.channelId,
          media_id: media.media_id,
          start_time: format(insertTime, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
          duration: durationSeconds
        }

        console.log('Insert media payload:', JSON.stringify(payload, null, 2))

        // Insert media at gap start (background)
        await ScheduleApi.insertMedia(payload)

        // Refresh day view to get real data (replaces optimistic item)
        await this.fetchDayPlaylistEvents(this.currentDate)

        this.$buefy.toast.open({
          message: `${media.title} inserted`,
          type: 'is-success',
          duration: 1500
        })
      } catch (error) {
        console.error('Failed to insert media:', error)

        // Remove optimistic item on failure
        this.events = this.events.filter(e => e.schedule_id !== optimisticId)

        // Check if it's a clash error
        if (error.response && error.response.status === 409) {
          this.$buefy.toast.open({
            message: 'Media conflicts with existing schedule items',
            type: 'is-danger',
            duration: 4000
          })
        } else {
          this.$buefy.toast.open({
            message: 'Failed to insert media. Please try again.',
            type: 'is-danger',
            duration: 3000
          })
        }
      }
    },

    async handleTimelineMediaReposition({ schedule_id, newStartTime, duration, insertIndex }) {
      console.log('Repositioning item:', schedule_id, 'to time:', newStartTime, 'at index:', insertIndex)

      // OPTIMISTIC UI: Update item position immediately so user sees instant feedback
      // Find the item being moved
      const itemIndex = this.events.findIndex(e => e.schedule_id === schedule_id)
      if (itemIndex === -1) {
        console.error('Item not found for reposition:', schedule_id)
        return
      }

      // Store original item state for rollback on failure
      const originalItem = { ...this.events[itemIndex] }
      const originalEvents = [...this.events]

      // Calculate new end time
      const durationSeconds = parseInt(duration) || parseInt(originalItem.duration_seconds) || 0
      const newEndTime = new Date(newStartTime.getTime() + (durationSeconds * 1000))

      // Update item position optimistically
      const updatedItem = {
        ...originalItem,
        start: format(newStartTime, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
        end: format(newEndTime, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
        _optimistic: true
      }

      // Remove from old position and insert at new position
      this.events.splice(itemIndex, 1)
      const newIdx = this.events.findIndex(e => new Date(e.start) > newStartTime)
      if (newIdx === -1) {
        this.events.push(updatedItem)
      } else {
        this.events.splice(newIdx, 0, updatedItem)
      }

      try {
        // CRITICAL: Use local ISO format without 'Z' suffix to prevent timezone shift
        // toISOString() converts to UTC which causes 2-hour offset on SAST timezone
        const payload = {
          start_time: format(newStartTime, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
          duration: duration,
          insert_index: insertIndex
        }

        console.log('Reposition payload:', JSON.stringify(payload, null, 2))

        // Call API to move/update the schedule item (background)
        await ScheduleApi.moveScheduleItem(schedule_id, payload)

        // Refresh day view to get real data (replaces optimistic positions)
        await this.fetchDayPlaylistEvents(this.currentDate)

        this.$buefy.toast.open({
          message: 'Item moved',
          type: 'is-success',
          duration: 1500
        })
      } catch (error) {
        console.error('Failed to reposition item:', error)

        // Rollback to original state on failure
        this.events = originalEvents

        // Show specific error message from backend if available (e.g., segment displacement error)
        const errorMessage = error.response?.data?.error || 'Failed to move item. Please try again.'

        this.$buefy.toast.open({
          message: errorMessage,
          type: 'is-danger',
          duration: 5000
        })
      }
    },

    // Handle date change from timeline navigation
    async handleDateChange(newDate) {
      this.currentDate = newDate
      await this.fetchDayPlaylistEvents(newDate)
    },

    // Handle timeline item click
    handleTimelineItemClick(item) {
      // Show item details in console for now
      console.log('Timeline item clicked:', item)

      // Could open a modal with item details in the future
      // this.$buefy.dialog.alert({
      //   title: item.title,
      //   message: `Start: ${new Date(item.start).toLocaleTimeString()}<br>End: ${new Date(item.end).toLocaleTimeString()}`
      // })
    },

    async handleTimelineItemDelete(item) {
      const self = this
      // Confirm deletion
      this.$buefy.dialog.confirm({
        title: 'Delete Item',
        message: `Are you sure you want to delete "${item.title}"?`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {
          try {
            await ScheduleApi.remove({ id: item.schedule_id })
            self.$buefy.toast.open({
              message: 'Item deleted successfully',
              type: 'is-success'
            })
            // Reload day view
            await self.fetchDayPlaylistEvents(self.currentDate)
          } catch (error) {
            console.error('Error deleting item:', error)
            self.$buefy.toast.open({
              message: 'Error deleting item',
              type: 'is-danger'
            })
          }
        }
      })
    },

    async handleTimelineBulkDelete(items) {
      // Confirmation is already handled in BroadcastTimeline component
      try {
        const ids = items.map(item => item.schedule_id)
        await ScheduleApi.removeMultiple(ids)
        this.$buefy.toast.open({
          message: `${items.length} item${items.length > 1 ? 's' : ''} deleted successfully`,
          type: 'is-success'
        })
        // Reload day view
        await this.fetchDayPlaylistEvents(this.currentDate)
      } catch (error) {
        console.error('Error bulk deleting items:', error)
        this.$buefy.toast.open({
          message: 'Error deleting items',
          type: 'is-danger'
        })
      }
    },

    // Media drag handlers for optimistic UI preview.
    // MediaLibraryPanel emits the whole media row; backend getAll computes
    // duration_seconds from duration_ms (probed, canonical) preferring it
    // over mediadata.mediaLength (operator-typed, often wrong — 45 of 52
    // shortform rows audited 2026-06-03 had both fields disagreeing).
    handleMediaDragStart(mediaData) {
      this.isDraggingMedia = true
      if (mediaData && mediaData.duration_seconds != null) {
        this.draggingMediaDuration = mediaData.duration_seconds
      } else if (mediaData && mediaData.duration_ms != null) {
        // Defensive: if backend was bypassed (legacy caller), derive from ms.
        this.draggingMediaDuration = Math.round(mediaData.duration_ms / 1000)
      } else if (mediaData && mediaData.mediadata) {
        // Last-resort fallback only when neither canonical field is present.
        // mediaLength unit is media_type-dependent — series=minutes, shortform=seconds.
        try {
          const data = typeof mediaData.mediadata === 'string'
            ? JSON.parse(mediaData.mediadata)
            : mediaData.mediadata
          if (data.mediaLength) {
            const ml = parseInt(data.mediaLength, 10)
            this.draggingMediaDuration = mediaData.media_type === 'series' ? ml * 60 : ml
          }
        } catch (e) {
          this.draggingMediaDuration = 0
        }
      } else {
        this.draggingMediaDuration = 0
      }
    },

    handleMediaDragEnd() {
      this.isDraggingMedia = false
      this.draggingMediaDuration = 0
    },

    // Replace mode handlers
    handleReplaceModeStart(selectedItems) {
      this.isReplaceMode = true
    },

    handleReplacementSelected(newMedia) {
      // Pass the replacement media to the timeline component
      if (this.$refs.timeline) {
        this.$refs.timeline.confirmReplace(newMedia)
      }
      this.isReplaceMode = false
    },

    async handleTimelineBulkReplace({ itemsToReplace, newMedia }) {
      // Confirmation is already handled in BroadcastTimeline component
      try {
        const scheduleIds = itemsToReplace.map(item => item.schedule_id)
        await ScheduleApi.bulkReplace({
          scheduleIds: scheduleIds,
          newMediaId: newMedia.media_id
        })
        this.$buefy.toast.open({
          message: `${itemsToReplace.length} item${itemsToReplace.length > 1 ? 's' : ''} replaced successfully`,
          type: 'is-success'
        })
        // Reload day view
        await this.fetchDayPlaylistEvents(this.currentDate)
      } catch (error) {
        console.error('Error bulk replacing items:', error)
        this.$buefy.toast.open({
          message: 'Error replacing items',
          type: 'is-danger'
        })
      }
      this.isReplaceMode = false
    },

    async handleCopyDayShortform({ sourceDate, destinationDate, shortformItems }) {
      try {
        const result = await ScheduleApi.copyDayShortform({
          channelId: this.channel.id,
          sourceDate: sourceDate,
          destinationDate: destinationDate,
          shortformItems: shortformItems
        })

        // Notify the timeline component of success
        if (this.$refs.timeline && this.$refs.timeline.onCopyDayComplete) {
          this.$refs.timeline.onCopyDayComplete(
            true,
            result.message || `Copied ${result.insertedCount} items successfully`
          )
        }

        // If destination is same as current view date, reload
        const currentDateStr = this.currentDate.toISOString().split('T')[0]
        if (destinationDate === currentDateStr) {
          await this.fetchDayPlaylistEvents(this.currentDate)
        }
      } catch (error) {
        console.error('Error copying day shortform:', error)

        // Notify the timeline component of failure
        if (this.$refs.timeline && this.$refs.timeline.onCopyDayComplete) {
          this.$refs.timeline.onCopyDayComplete(
            false,
            error.response?.data?.err || 'Failed to copy playlist'
          )
        }
      }
    }
  }
}
</script>

<style>
/* Stale Data Warning Banner */
.stale-data-warning {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%);
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);
}

.stale-warning-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.stale-warning-content .icon {
  color: #856404;
}

.stale-warning-text {
  flex: 1;
  color: #856404;
  font-weight: 500;
  min-width: 200px;
}

.stale-warning-actions {
  display: flex;
  gap: 8px;
}

.vuecal__no-event {
  display: none;
}
/* Hide VueCal's default event time display in tooltips (shows incorrect timezone) */
.vuecal__event-time {
  display: none !important;
}

/* Dark header styling for week view - softer dark grey */
.vuecal__header {
  background-color: #4a4a4a !important;
  border-bottom: 3px solid #00d1b2 !important;
}
.vuecal__title-bar {
  background-color: #4a4a4a !important;
  color: #fff !important;
}
/* White arrows for week navigation */
.vuecal__arrow {
  color: #fff !important;
}
.vuecal__arrow:hover {
  color: #00d1b2 !important;
}
.vuecal__weekdays-headings {
  background-color: #4a4a4a !important;
  border-bottom: 2px solid #5a5a5a !important;
}
.vuecal__heading {
  background-color: #4a4a4a !important;
  color: #fff !important;
  font-weight: 600;
  border-color: #5a5a5a !important;
  /* White vertical dividers between days */
  border-right: 1px solid rgba(255, 255, 255, 0.3) !important;
}
.vuecal__heading:last-child {
  border-right: none !important;
}
/* NUCLEAR: Force ALL text in weekday headings to be white */
.vuecal .vuecal__weekdays-headings .vuecal__heading,
.vuecal .vuecal__weekdays-headings .vuecal__heading *,
.vuecal .vuecal__weekdays-headings .vuecal__heading *::before,
.vuecal .vuecal__weekdays-headings .vuecal__heading *::after,
.vuecal__weekdays-headings .vuecal__heading,
.vuecal__weekdays-headings .vuecal__heading * {
  color: #fff !important;
  -webkit-text-fill-color: #fff !important;
}
.vuecal__heading small {
  color: #ccc !important;
  -webkit-text-fill-color: #ccc !important;
  display: block !important;
}

/* Grid lines - completely reset and apply clean single borders */
.vuecal__bg,
.vuecal__bg *,
.vuecal__cell,
.vuecal__cell *,
.vuecal__cell::before,
.vuecal__cell::after,
.vuecal__cell-content,
.vuecal__cell-content::before,
.vuecal__cell-content::after {
  border: none !important;
  box-shadow: none !important;
}
/* Apply single clean borders only to cells */
.vuecal__cell {
  border-right: 1px solid #ccc !important;
  border-bottom: 1px solid #888 !important;
}
/* Remove border from last column */
.vuecal__cell:last-child {
  border-right: none !important;
}
/* Outer container border */
.vuecal__cells {
  border: 1px solid #4a4a4a !important;
}
/* Time column styling */
.vuecal__time-column {
  background-color: #f5f5f5 !important;
  border-right: 1px solid #888 !important;
}
.vuecal__time-cell {
  border-bottom: 1px solid #888 !important;
  color: #363636;
  font-weight: 500;
  font-size: 11px;
}
/* Half-hour line inside cells */
.vuecal__cell-split {
  border-top: 1px solid #ddd !important;
}

.vuecal__cell .vuecal__event * {
  font-size: 12px;
}
.vuecal__event-title {
  font-weight: bold;
}
.episode-title-subtitle {
  font-weight: normal;
  font-size: 10px;
  opacity: 0.85;
  margin-top: 1px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.week-view-event {
  position: relative;
}
.tx-status-dot {
  position: absolute;
  bottom: 3px;
  left: 3px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.3);
  pointer-events: none;
}
.tx-status-dot.tx-ready {
  background-color: #22c55e;
}
.tx-status-dot.tx-missing {
  background-color: #ef4444;
}
.vuecal__event {
  color: #fff;
  border: 1px solid #666;
  opacity: 0.8;
}
.vuecal__event.series-1 {
  background-color: #3fe0d0;
}
.vuecal__event.series-2 {
  background-color: #4169e1;
}
.vuecal__event.series-3 {
  background-color: #6595ec;
}
.vuecal__event.series-4 {
  background-color: #0047ab;
}
.vuecal__event.series-5 {
  background-color: #aed8e6;
}
.vuecal__event.series-6 {
  background-color: #89ceef;
}
.vuecal__event.series-7 {
  background-color: #0ebab5;
}
.vuecal__event.series-8 {
  background-color: #0d52ba;
}
.vuecal__event.series-9 {
  background-color: #0073cf;
}
.vuecal__event.series-0 {
  background-color: #2a52be;
}
.vuecal__event.movie-1 {
  background-color: #8d021f;
}
.vuecal__event.movie-2 {
  background-color: #5e1914;
}
.vuecal__event.movie-3 {
  background-color: #b80f0a;
}
.vuecal__event.movie-4 {
  background-color: #f20d09;
}
.vuecal__event.movie-5 {
  background-color: #bf0a30;
}
.vuecal__event.movie-6 {
  background-color: #ca3433;
}
.vuecal__event.movie-7 {
  background-color: #d21f3c;
}
.vuecal__event.movie-8 {
  background-color: #b43757;
}
.vuecal__event.movie-9 {
  background-color: #ff0800;
}
.vuecal__event.movie-0 {
  background-color: #7e191b;
}
/* Special hours (managed slots) with semi-transparent backgrounds to let grid lines show through */
.vuecal__cell .special-hours-1 {
  background: rgba(255, 255, 255, 0.7);
}
.vuecal__cell .special-hours-2 {
  background: rgba(239, 239, 239, 0.7);
}
.vuecal__cell .special-hours-3 {
  background: rgba(238, 238, 238, 0.7);
}
.vuecal__cell .special-hours-4 {
  background: rgba(221, 221, 221, 0.7);
}
.vuecal__cell .special-hours-5 {
  background: rgba(204, 204, 204, 0.7);
}
.vuecal__cell .special-hours-6 {
  background: rgba(187, 187, 187, 0.7);
}
.vuecal__cell .special-hours-7 {
  background: rgba(170, 170, 170, 0.7);
}
.vuecal__cell .special-hours-8 {
  background: rgba(153, 153, 153, 0.7);
}
.vuecal__cell .special-hours-9 {
  background: rgba(152, 152, 152, 0.7);
}
.vuecal__cell .special-hours-0 {
  background: rgba(151, 151, 151, 0.7);
}

/* VueCal fixes for proper display */
.vuecal {
  border: 1px solid #4a4a4a;
  border-radius: 4px;
}

/* Ensure grid lines show through special hours backgrounds */
.vuecal__special-hours {
  pointer-events: none;
}

/* Playlist item styles for day view */
.vuecal__event.segment {
  background-color: #e3f2fd !important;
  border-left: 4px solid #2196f3 !important;
  color: #1565c0 !important;
}

.vuecal__event.break {
  background-color: #ffebee !important;
  border-left: 6px solid #f44336 !important;
  color: #c62828 !important;
  font-style: italic;
  font-weight: 600 !important;
}

.break-icon {
  font-size: 14px;
  margin-right: 4px;
}

/* Ensure event titles are visible and wrap in day view */
.vuecal__event-title {
  overflow: visible !important;
  white-space: normal !important;
  word-wrap: break-word !important;
  font-weight: 500;
}

/* Day view controls */
.day-view-controls {
  display: flex;
  justify-content: flex-end;
  padding: 10px 10px 5px 10px;
  background: #f5f5f5;
  border-radius: 4px 4px 0 0;
  margin-bottom: -1px;
}

.zoom-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.zoom-label {
  font-size: 13px;
  font-weight: 600;
  color: #363636;
}

.zoom-indicator {
  font-size: 12px;
  color: #7a7a7a;
  min-width: 50px;
  text-align: center;
}

/* Day view layout with media panel */
.day-view-layout {
  display: grid;
  grid-template-columns: 280px 1fr;
  grid-template-rows: minmax(0, 1fr);
  gap: 0;
  height: 820px;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  overflow: hidden;
}

/* Grid items default to min-height: auto (refuse to shrink below content).
   With a long media library list this expanded the grid row beyond 820px,
   breaking the inner scroll of both the media library and the timeline.
   min-height: 0 + min-width: 0 lets the grid track constrain the children. */
.day-view-layout > * {
  min-height: 0;
  min-width: 0;
}

.calendar-section {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Day view event layout */
.day-view-event {
  display: flex;
  flex-direction: column;
  padding: 4px 6px !important;
  font-size: 12px !important;
  line-height: 1.3 !important;
  height: 100%;
  justify-content: center;
}

/* Remove min-height constraint so events render at correct duration
   The old min-height: 50px was causing events to visually extend beyond their actual end time in week view */
.vuecal__event {
  min-height: 0 !important;
  max-height: none !important;
}

.event-main-title {
  font-weight: 600;
  margin-bottom: 2px;
}

.event-meta {
  font-size: 10px;
  margin-bottom: 2px;
  opacity: 0.9;
}

.event-duration {
  font-size: 10px;
  opacity: 0.8;
  font-weight: 400;
}

.vuecal__event.commercial {
  background-color: #fff3e0 !important;
  border-left: 4px solid #ff9800 !important;
}

.vuecal__event.promo {
  background-color: #f3e5f5 !important;
  border-left: 4px solid #9c27b0 !important;
}

.vuecal__event.filler {
  background-color: #f1f8e9 !important;
  border-left: 4px solid #8bc34a !important;
}

.vuecal__event.gfx {
  background-color: #e0f2f1 !important;
  border-left: 4px solid #009688 !important;
}

/* Drop zones for drag-and-drop */
.drop-zones-overlay {
  position: absolute;
  top: 0;
  left: 60px;  /* After time column */
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1000;
}

.drop-zone {
  position: absolute;
  left: 0;
  right: 15px;
  border: 2px dashed #4caf50;
  background-color: rgba(76, 175, 80, 0.1);
  pointer-events: all;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  cursor: copy;
}

.drop-zone:hover {
  background-color: rgba(76, 175, 80, 0.2);
  border-color: #2e7d32;
  border-width: 3px;
}

.drop-zone-label {
  font-size: 11px;
  font-weight: 600;
  color: #2e7d32;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 8px;
  border-radius: 3px;
  white-space: nowrap;
}
</style>
